// src/components/UserProfileView.jsx
// In-app overlay for viewing another user's profile
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUser, API_BASE } from "../context/UserContext";
// import CuratedLists from "./CuratedLists"; // eslint-disable-line no-unused-vars
import { STATUS_LABEL, STATUS_EMOJI, STATUS_COLOR } from "../constants";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary: "#655d54", primaryDim: "#595149",
  primaryContainer: "#ede0d5",
  bg: "#faf9f6", surfaceLow: "#f4f4f0",
  surfaceLowest: "#ffffff",
  container: "#edeeea",
  onSurface: "#2f3430", onSurfaceVariant: "#5c605c",
  outlineVariant: "#8a8e8a", error: "#9e422c",
};

export default function UserProfileView({ nickname, onClose, embedded = false, onViewMap, onPlaceClick }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const [profile, setProfile] = useState(null);
  const [places, setPlaces] = useState([]);
  const [curatedLists, setCuratedLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [followStatus, setFollowStatus] = useState("none"); // "none" | "pending" | "accepted"
  const [followLoading, setFollowLoading] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const isMobile = window.innerWidth <= 768;

  const [isPrivateNoAccess, setIsPrivateNoAccess] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError("");
    setIsPrivateNoAccess(false);
    Promise.all([
      axios.get(`${API_BASE}/users/${nickname}`),
      axios.get(`${API_BASE}/users/${nickname}/public-places`).catch((e) => {
        if (e.response?.status === 403) setIsPrivateNoAccess(true);
        return { data: [] };
      }),
      axios.get(`${API_BASE}/lists/public/${nickname}`).catch(() => ({ data: [] })),
    ])
      .then(([profileRes, placesRes, listsRes]) => {
        setProfile(profileRes.data);
        setPlaces(placesRes.data);
        setCuratedLists(listsRes.data);
      })
      .catch(() => {
        setError(t("follow.not_found"));
      })
      .finally(() => setLoading(false));
  }, [nickname]); // eslint-disable-line

  // Check follow status (both accepted and pending)
  useEffect(() => {
    if (!user || !profile) return;
    Promise.all([
      axios.get(`${API_BASE}/follows/me/following`),
      axios.get(`${API_BASE}/follows/me/pending`),
    ]).then(([followingRes, pendingRes]) => {
      if (followingRes.data.some((f) => (f.id || f.user_id) === profile.id)) {
        setFollowStatus("accepted");
      } else if (pendingRes.data.some((f) => (f.id || f.user_id) === profile.id)) {
        setFollowStatus("pending");
      } else {
        setFollowStatus("none");
      }
    }).catch(() => {});
  }, [user, profile]);

  // Initialize map
  useEffect(() => {
    if (!places.length || !mapContainerRef.current || !window.google?.maps) return;
    if (mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    places.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));

    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: bounds.getCenter(),
      zoom: 13,
      mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      zoomControl: false,
      gestureHandling: "none",
      mapId: process.env.REACT_APP_GOOGLE_MAP_ID || "DEMO_MAP_ID",
    });
    if (places.length > 1) map.fitBounds(bounds, 30);
    else map.setZoom(14);

    places.forEach((p) => {
      const color = STATUS_COLOR[p.status]?.color || C.primary;
      const content = document.createElement("div");
      content.innerHTML = `<div style="width:8px;height:8px;border-radius:50%;background:${color};border:1.5px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.2);"></div>`;
      new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: p.lat, lng: p.lng },
        map,
        content,
      });
    });
    mapRef.current = map;

    return () => { mapRef.current = null; };
  }, [places]);

  const handleFollow = async () => {
    if (!user || !profile) return;
    setFollowLoading(true);
    try {
      if (followStatus === "accepted" || followStatus === "pending") {
        await axios.delete(`${API_BASE}/follows/${profile.id}`);
        setFollowStatus("none");
        // If private account, clear places (no access)
        if (!profile.is_public) {
          setPlaces([]);
          setCuratedLists([]);
          setIsPrivateNoAccess(true);
        }
      } else {
        const res = await axios.post(`${API_BASE}/follows/${profile.id}`);
        const newStatus = res.data.status === "accepted" ? "accepted" : "pending";
        setFollowStatus(newStatus);
        // If accepted, reload places
        if (newStatus === "accepted") {
          axios.get(`${API_BASE}/users/${nickname}/public-places`)
            .then((r) => { setPlaces(r.data); setIsPrivateNoAccess(false); }).catch(() => {});
        }
      }
    } catch { alert(t("follow.request_failed")); } finally { setFollowLoading(false); }
  };

  const counts = {};
  places.forEach((p) => { counts[p.status] = (counts[p.status] || 0) + 1; });

  return (
    <div style={{
      ...(embedded ? { background: C.bg } : {
        position: "fixed", inset: 0, zIndex: 60,
        background: C.bg, overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }),
    }}>
      <div style={{
        maxWidth: 520, margin: "0 auto",
        padding: embedded ? "0 16px 40px" : (isMobile ? "0 0 100px" : "0 28px 60px"),
      }}>
        {/* Top header */}
        <div style={{
          position: "sticky", top: 0, zIndex: 10,
          background: "rgba(250,249,246,0.92)",
          backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
          padding: embedded ? "14px 0" : (isMobile ? "16px 18px" : "20px 0"),
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <button onClick={onClose} style={{
            display: "flex", alignItems: "center", gap: 4,
            border: "none", background: "none", cursor: "pointer",
            fontFamily: FL, fontSize: embedded ? 11 : 13, fontWeight: 600, color: C.primary, padding: 0,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
            Back
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <p style={{ fontFamily: FL, fontSize: 12, color: C.outlineVariant }}>Loading...</p>
          </div>
        )}

        {error && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 16, color: C.outlineVariant }}>{error}</p>
          </div>
        )}

        {profile && !error && (
          <>
            {/* Profile card */}
            <div style={{
              background: C.surfaceLowest, borderRadius: embedded ? 10 : 12,
              padding: embedded ? "14px 14px" : "18px 20px",
              marginBottom: embedded ? 10 : 12, boxShadow: "0 1px 8px rgba(47,52,48,0.05)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: embedded ? 12 : 16, marginBottom: embedded ? 12 : 16 }}>
                <div style={{
                  width: embedded ? 40 : 52, height: embedded ? 40 : 52, borderRadius: "50%", flexShrink: 0,
                  background: profile.profile_photo_url
                    ? `url(${profile.profile_photo_url}) center/cover`
                    : `linear-gradient(135deg, ${C.primaryDim}, ${C.primary})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: FH, fontStyle: "italic",
                  fontSize: embedded ? 17 : 22, color: "#fff6ef", fontWeight: 700,
                }}>
                  {!profile.profile_photo_url && profile.nickname?.[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ margin: "0 0 4px", fontFamily: FH, fontSize: embedded ? 15 : 19, fontWeight: 700, color: C.onSurface }}>
                    {profile.nickname}
                  </h2>
                  <div style={{ display: "flex", gap: 12, fontFamily: FL, fontSize: 11, color: C.onSurfaceVariant }}>
                    <span>{profile.place_count} places</span>
                    <span>{profile.follower_count} followers</span>
                    <span>{profile.following_count} following</span>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
                    {profile.instagram_url && (
                      <a href={profile.instagram_url} target="_blank" rel="noreferrer"
                        style={{ fontFamily: FL, fontSize: 11, color: "#E1306C", textDecoration: "none", fontWeight: 600 }}>
                        Instagram
                      </a>
                    )}
                    {profile.blog_url && (
                      <a href={profile.blog_url} target="_blank" rel="noreferrer"
                        style={{ fontFamily: FL, fontSize: 11, color: "#3B8BD4", textDecoration: "none", fontWeight: 600 }}>
                        Blog
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Follow button */}
              {user && profile.id !== user.user_id && (
                <button onClick={handleFollow} disabled={followLoading}
                  style={{
                    width: "100%", padding: embedded ? "9px" : "11px", border: "none", borderRadius: 8,
                    background: followStatus === "none" ? C.primary : "none",
                    color: followStatus === "none" ? "#fff6ef" : followStatus === "pending" ? "#BA7517" : C.outlineVariant,
                    borderWidth: followStatus === "none" ? 0 : 1,
                    borderStyle: "solid",
                    borderColor: followStatus === "pending" ? "rgba(186,117,23,0.3)" : "rgba(101,93,84,0.15)",
                    fontFamily: FL, fontSize: embedded ? 11 : 12, fontWeight: 700,
                    cursor: followLoading ? "not-allowed" : "pointer",
                    opacity: followLoading ? 0.6 : 1,
                    transition: "all 0.15s",
                  }}
                >
                  {followStatus === "accepted" ? t("profile.following") : followStatus === "pending" ? t("follow.requested") : t("follow.follow")}
                </button>
              )}
            </div>

            {/* Mini map */}
            {places.length > 0 && (
              <div style={{
                background: C.surfaceLowest, borderRadius: 12, padding: "18px 20px",
                marginBottom: 12, boxShadow: "0 1px 8px rgba(47,52,48,0.05)",
              }}>
                <p style={{
                  margin: "0 0 12px", fontFamily: FL, fontSize: 9, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant,
                }}>
                  {profile.nickname}'s Map
                </p>
                {(() => {
                  const canViewMap = onViewMap && followStatus === "accepted";
                  return (
                    <>
                      <div
                        onClick={() => canViewMap && onViewMap(profile.id)}
                        style={{ cursor: canViewMap ? "pointer" : "default" }}
                      >
                        <div ref={mapContainerRef} style={{
                          width: "100%", height: embedded ? 130 : 160, borderRadius: 10,
                          overflow: "hidden", background: C.surfaceLow, marginBottom: 12,
                          pointerEvents: "none",
                        }} />
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                        {Object.entries(counts).map(([status, count]) => (
                          <span key={status} style={{
                            fontFamily: FL, fontSize: 11, color: C.onSurfaceVariant,
                            display: "flex", alignItems: "center", gap: 4,
                          }}>
                            {STATUS_EMOJI[status] || "📍"} {count}
                          </span>
                        ))}
                        {canViewMap && (
                          <button onClick={() => onViewMap(profile.id)} style={{
                            marginLeft: "auto", display: "flex", alignItems: "center", gap: 3,
                            background: "none", border: "none", cursor: "pointer", padding: 0,
                            fontFamily: FL, fontSize: 11, fontWeight: 600, color: C.primary,
                          }}>
                            Tap to explore
                            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
                          </button>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Curated lists */}
            {curatedLists.length > 0 && (
              <div style={{
                background: C.surfaceLowest, borderRadius: 12, padding: "18px 20px",
                marginBottom: 12, boxShadow: "0 1px 8px rgba(47,52,48,0.05)",
              }}>
                <p style={{
                  margin: "0 0 12px", fontFamily: FL, fontSize: 9, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant,
                }}>
                  Curated Lists
                </p>
                {curatedLists.map((cl) => (
                  <a key={cl.id} href={`/list/${cl.id}`} target="_blank" rel="noreferrer"
                    style={{
                      display: "block", padding: "10px 12px",
                      borderRadius: 8, background: C.surfaceLow,
                      marginBottom: 6, textDecoration: "none",
                      transition: "background 0.15s",
                    }}
                  >
                    <p style={{ margin: 0, fontFamily: FH, fontSize: embedded ? 12 : 14, fontWeight: 600, color: C.onSurface }}>
                      {cl.title}
                    </p>
                    <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 10, color: C.outlineVariant }}>
                      {cl.item_count} places {cl.description ? `· ${cl.description}` : ""}
                    </p>
                  </a>
                ))}
              </div>
            )}

            {/* Places (place cards) */}
            {places.length > 0 && (
              <div style={{
                background: C.surfaceLowest, borderRadius: 12, padding: "18px 20px",
                marginBottom: 12, boxShadow: "0 1px 8px rgba(47,52,48,0.05)",
              }}>
                <p style={{
                  margin: "0 0 12px", fontFamily: FL, fontSize: 9, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant,
                }}>
                  {profile.nickname}'s places — {places.length}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {places.map((p) => {
                    const sc = STATUS_COLOR[p.status];
                    const photo = p.photo_url;
                    return (
                      <div key={p.id} onClick={() => onPlaceClick?.(p)} style={{
                        borderRadius: 10, overflow: "hidden",
                        background: C.surfaceLow,
                        cursor: onPlaceClick ? "pointer" : "default",
                        transition: "background 0.1s",
                      }}>
                        {photo && (
                          <div style={{ width: "100%", aspectRatio: "5/4", overflow: "hidden", background: "#f0efec" }}>
                            <img src={photo} alt={p.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                          </div>
                        )}
                        <div style={{ padding: embedded ? "8px 12px" : "10px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: p.memo ? 6 : 0 }}>
                            <span style={{
                              fontFamily: FL, fontSize: 10, fontWeight: 600,
                              padding: "2px 7px", borderRadius: 4,
                              background: sc?.bg || C.surfaceLow, color: sc?.color || C.onSurfaceVariant,
                            }}>
                              {STATUS_EMOJI[p.status]} {STATUS_LABEL[p.status]}
                            </span>
                            {p.rating > 0 && (
                              <span style={{
                                fontFamily: FL, fontSize: 10, padding: "2px 7px",
                                background: C.primaryContainer, color: C.primary,
                                borderRadius: 4, fontWeight: 600,
                              }}>
                                {"★".repeat(p.rating)}{"☆".repeat(5 - p.rating)}
                              </span>
                            )}
                            <span style={{ fontFamily: FH, fontSize: embedded ? 12 : 13, fontWeight: 600, color: C.onSurface }}>
                              {p.name}
                            </span>
                          </div>
                          {p.memo && (
                            <p style={{
                              margin: "4px 0 0", fontFamily: FH, fontStyle: "italic",
                              fontSize: 12, color: C.onSurfaceVariant, lineHeight: 1.6,
                            }}>
                              "{p.memo}"
                            </p>
                          )}
                          {p.address && (
                            <p style={{
                              margin: "4px 0 0", fontFamily: FL, fontSize: 10, color: C.outlineVariant,
                              display: "flex", alignItems: "center", gap: 3,
                            }}>
                              <span className="material-symbols-outlined" style={{ fontSize: 11 }}>location_on</span>
                              {p.address}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {places.length === 0 && !error && (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <span className="material-symbols-outlined" style={{ fontSize: 48, color: C.outlineVariant }}>
                  {isPrivateNoAccess ? "lock" : "location_on"}
                </span>
                <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 16, color: C.onSurfaceVariant, marginTop: 12 }}>
                  {isPrivateNoAccess ? t("follow.private_profile") : t("follow.no_public_places")}
                </p>
                {isPrivateNoAccess && (
                  <p style={{ fontFamily: FL, fontSize: 12, color: C.outlineVariant, marginTop: 4 }}>
                    Send a follow request to see their places
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
